import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import useHiddenModules from "@/lib/useHiddenModules";
import { useOpenTaskCounts, OpenTasksBadge } from "@/components/OpenTasksBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Plus, Briefcase, Pencil, Trash2, Building2, Users, Search, ArrowRight } from "lucide-react";
import ClientWizard from "@/components/wizards/ClientWizard";
import { toast } from "sonner";
import { fmtEur, photoSrc, COLOR, COLOR_LABEL, DOT } from "@/lib/format";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [wizard, setWizard] = useState(false);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const noPay = useHiddenModules().includes("tab-betalingen");
  const taskCounts = useOpenTaskCounts(noPay, true);
  const boxRef = useRef(null);

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = clients.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
  const suggestions = q ? filtered.slice(0, 6) : [];

  const doDelete = async () => {
    try { await api.delete(`/clients/${del.id}`); toast.success("Verwijderd"); load(); } catch { toast.error("Verwijderen mislukt"); }
    setDel(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#0F1E3D]">Opdrachtgevers</h1>
          <p className="text-slate-500">Klik op een opdrachtgever voor al zijn panden, huurders en factoring.</p>
        </div>
        <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => { setEdit(null); setWizard(true); }} data-testid="add-client-wizard-button">
          <Plus className="h-4 w-4 mr-2" /> Nieuwe opdrachtgever
        </Button>
      </div>

      <div className="relative max-w-md" ref={boxRef}>
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input className="pl-9" placeholder="Zoek opdrachtgever (autocomplete)" value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} data-testid="client-search-input" />
        {open && suggestions.length > 0 && (
          <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg overflow-hidden" data-testid="client-search-suggestions">
            {suggestions.map((c) => (
              <button key={c.id} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 border-b last:border-0" onClick={() => navigate(`/app/opdrachtgevers/${c.id}`)} data-testid={`client-suggestion-${c.id}`}>
                {!noPay && <span className={`h-2 w-2 rounded-full ${DOT[c.status_color]}`} />} {c.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {clients.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Briefcase className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 mb-4">Nog geen opdrachtgevers.</p>
          <Button onClick={() => setWizard(true)} className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white"><Plus className="h-4 w-4 mr-2" /> Toevoegen</Button>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((c) => (
            <div key={c.id} onClick={() => navigate(`/app/opdrachtgevers/${c.id}`)} data-testid={`client-row-${c.id}`}
              className="group flex items-center justify-between gap-4 p-4 rounded-xl border bg-white hover:shadow-md hover:border-slate-300 transition-all cursor-pointer">
              <div className="flex items-center gap-4 min-w-0">
                <div className="h-12 w-12 rounded-lg border bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
                  {c.photo_url ? <img src={photoSrc(c.photo_url)} alt="" className="h-full w-full object-cover" /> : <Briefcase className="h-5 w-5 text-slate-300" />}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-3">
                    <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {c.property_count}</span>
                    <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {c.tenant_count}</span>
                    {c.factoring_count > 0 && <span>· {c.factoring_count} factoring</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-5">
                {!noPay && <div className="text-right hidden sm:block"><div className="text-xs text-slate-400">Openstaand</div><div className="font-semibold text-slate-800">{fmtEur(c.outstanding)}</div></div>}
                {!noPay && <div className="text-right hidden sm:block"><div className="text-xs text-slate-400">Te laat</div><div className={`font-semibold ${c.overdue_amount > 0 ? "text-red-600" : "text-slate-500"}`}>{fmtEur(c.overdue_amount)}</div></div>}
                {noPay ? <OpenTasksBadge n={taskCounts[c.id] || 0} tid={c.id} /> : <Badge className={COLOR[c.status_color]}>{COLOR_LABEL[c.status_color]}</Badge>}
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEdit(c); setWizard(true); }} data-testid={`client-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => setDel(c)} data-testid={`client-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </div>
            </div>
          ))}
        </div>
      )}

      <ClientWizard open={wizard} onOpenChange={setWizard} initial={edit} onSaved={load} />
      <AlertDialog open={!!del} onOpenChange={() => setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Opdrachtgever verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>Weet u zeker dat u "{del?.name}" wilt verwijderen?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-client">Verwijderen</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
